import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Heart, Facebook, Instagram, Youtube, Linkedin, MapPin, Mail, Phone } from "lucide-react";
import { api } from "@/lib/api";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/programmes", label: "Programmes" },
    { to: "/impact", label: "Impact" },
    { to: "/gallery", label: "Gallery" },
    { to: "/media", label: "Media" },
    { to: "/csr", label: "CSR" },
    { to: "/transparency", label: "Transparency" },
    { to: "/contact", label: "Contact" },
];

function Header({ settings }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const loc = useLocation();

    useEffect(() => setOpen(false), [loc.pathname]);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
                scrolled ? "backdrop-saturate-150 backdrop-blur-xl bg-white/85 border-b border-black/5" : "bg-white/70 backdrop-blur"
            }`}
        >
            <div className="container-hero flex items-center justify-between h-20 md:h-24">
                <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
                    <img
                        src="https://customer-assets-39nsmqrw.emergentagent.net/job_kabir-ngo-platform/artifacts/kzxgbp73_kabir%20logo%202.png"
                        alt="Kabir Foundation"
                        className="h-16 md:h-20 w-auto object-contain"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-3 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                                    isActive
                                        ? "text-brand-forest bg-brand-stone"
                                        : "text-slate-700 hover:text-brand-forest hover:bg-brand-stone/60"
                                }`
                            }
                            end={n.to === "/"}
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-2">
                    <Link
                        to="/donate"
                        data-testid="header-donate-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-forest text-white text-sm font-semibold hover:bg-brand-forestHover hover:-translate-y-0.5 transition-transform duration-200 shadow-sm"
                    >
                        <Heart size={16} strokeWidth={2} />
                        Donate
                    </Link>
                </div>

                <button
                    data-testid="mobile-menu-btn"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden p-2 rounded-md hover:bg-brand-stone"
                    aria-label="Menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-black/5 bg-white">
                    <div className="container-hero py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-3 py-2.5 rounded-md text-sm font-medium ${
                                        isActive ? "bg-brand-stone text-brand-forest" : "text-slate-700"
                                    }`
                                }
                                end={n.to === "/"}
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/donate"
                            data-testid="mobile-donate-btn"
                            className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand-forest text-white text-sm font-semibold"
                        >
                            <Heart size={16} /> Donate Now
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}

function Footer({ settings }) {
    const s = settings || {};
    return (
        <footer className="mt-24 bg-[#1F1F1F] text-slate-300" data-testid="site-footer">
            <div className="container-hero py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-4">
                    <div className="flex items-center gap-4 mb-4">
                        <img
                            src="https://customer-assets-39nsmqrw.emergentagent.net/job_kabir-ngo-platform/artifacts/kzxgbp73_kabir%20logo%202.png"
                            alt="Kabir Foundation"
                            className="h-24 w-auto object-contain bg-white rounded-lg p-2"
                        />
                    </div>
                    <p className="text-sm leading-relaxed text-slate-400 max-w-sm">
                        A grassroots non-profit working in Bundelkhand across education, digital literacy,
                        women empowerment and community health since 2013.
                    </p>
                    <div className="flex items-center gap-3 mt-6">
                        {s.facebook_url && <a href={s.facebook_url} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 hover:bg-brand-leaf transition-colors duration-200"><Facebook size={16} /></a>}
                        {s.instagram_url && <a href={s.instagram_url} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 hover:bg-brand-leaf transition-colors duration-200"><Instagram size={16} /></a>}
                        {s.youtube_url && <a href={s.youtube_url} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 hover:bg-brand-leaf transition-colors duration-200"><Youtube size={16} /></a>}
                        {s.linkedin_url && <a href={s.linkedin_url} target="_blank" rel="noreferrer" className="p-2 rounded-full bg-white/5 hover:bg-brand-leaf transition-colors duration-200"><Linkedin size={16} /></a>}
                    </div>
                </div>

                <div className="md:col-span-3">
                    <div className="label-eyebrow text-slate-500 mb-4">Explore</div>
                    <ul className="space-y-2 text-sm">
                        {["about", "programmes", "impact", "gallery", "media"].map((k) => (
                            <li key={k}><Link to={`/${k}`} className="hover:text-white transition-colors duration-200 capitalize">{k}</Link></li>
                        ))}
                    </ul>
                </div>
                <div className="md:col-span-2">
                    <div className="label-eyebrow text-slate-500 mb-4">Support</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/donate" className="hover:text-white transition-colors duration-200">Donate</Link></li>
                        <li><Link to="/csr" className="hover:text-white transition-colors duration-200">CSR</Link></li>
                        <li><Link to="/contact" className="hover:text-white transition-colors duration-200">Volunteer</Link></li>
                        <li><Link to="/transparency" className="hover:text-white transition-colors duration-200">Transparency</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <div className="label-eyebrow text-slate-500 mb-4">Reach us</div>
                    <ul className="space-y-3 text-sm text-slate-400">
                        <li className="flex gap-2"><MapPin size={16} className="mt-0.5 flex-shrink-0" /><span>{s.address_line1}<br />{s.address_line2}</span></li>
                        <li className="flex gap-2"><Mail size={16} className="mt-0.5" /><a href={`mailto:${s.contact_email}`} className="hover:text-white transition-colors duration-200">{s.contact_email}</a></li>
                        <li className="flex gap-2"><Phone size={16} className="mt-0.5" /><a href={`tel:${s.contact_phone}`} className="hover:text-white transition-colors duration-200">{s.contact_phone}</a></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/5">
                <div className="container-hero py-5 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-slate-500">
                    <div>© {new Date().getFullYear()} Kabir Foundation. All rights reserved.</div>
                    <div className="flex items-center gap-4">
                        <Link to="/transparency" className="hover:text-slate-300">80G · 12A · CSR-1</Link>
                        <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-slate-300">Admin</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FloatingSocials({ settings }) {
    const s = settings || {};
    const wa = s.whatsapp_number;
    const waUrl = wa
        ? `https://wa.me/${wa}?text=${encodeURIComponent("Hello Kabir Foundation, I'd like to know more.")}`
        : null;

    const buttons = [
        wa && {
            key: "whatsapp",
            href: waUrl,
            label: "Chat on WhatsApp",
            bg: "bg-[#25D366] hover:bg-[#1FB855]",
            icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.99L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.19-1.24-6.19-3.48-8.52ZM12 22a9.9 9.9 0 0 1-5.06-1.38l-.36-.21-3.67.96.98-3.58-.24-.37A9.94 9.94 0 0 1 2 12c0-5.52 4.48-10 10-10s10 4.48 10 10-4.48 10-10 10Zm5.47-7.5c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.94 1.16-.17.2-.34.22-.64.07-.3-.15-1.26-.46-2.4-1.47-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.34.44-.51.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.5-.17 0-.37-.02-.57-.02-.2 0-.51.07-.78.37-.27.3-1.02.99-1.02 2.41 0 1.42 1.05 2.79 1.2 2.98.15.2 2.07 3.16 5.02 4.43.7.3 1.25.48 1.68.61.7.22 1.34.19 1.85.11.56-.08 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z" />
                </svg>
            ),
        },
        s.facebook_url && {
            key: "facebook",
            href: s.facebook_url,
            label: "Follow on Facebook",
            bg: "bg-[#1877F2] hover:bg-[#0F5FCC]",
            icon: <Facebook size={20} strokeWidth={2.25} />,
        },
        s.youtube_url && {
            key: "youtube",
            href: s.youtube_url,
            label: "Subscribe on YouTube",
            bg: "bg-[#FF0000] hover:bg-[#CC0000]",
            icon: <Youtube size={20} strokeWidth={2.25} />,
        },
    ].filter(Boolean);

    if (buttons.length === 0) return null;
    return (
        <div
            data-testid="floating-socials"
            className="fixed bottom-6 right-6 z-30 flex flex-col gap-3"
        >
            {buttons.map((b) => (
                <a
                    key={b.key}
                    href={b.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={b.label}
                    data-testid={`floating-${b.key}`}
                    className={`group relative ${b.bg} text-white rounded-full p-3.5 shadow-lg hover:-translate-y-0.5 transition-transform duration-200`}
                >
                    {b.icon}
                    <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-3 whitespace-nowrap px-3 py-1.5 rounded-md bg-brand-ink text-white text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        {b.label}
                    </span>
                </a>
            ))}
        </div>
    );
}


export default function PublicLayout() {
    const [settings, setSettings] = useState({});
    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen flex flex-col noise-bg">
            <Header settings={settings} />
            <main className="pt-20 md:pt-24 flex-1 relative z-10">
                <Outlet context={{ settings }} />
            </main>
            <Footer settings={settings} />
            <FloatingSocials settings={settings} />
        </div>
    );
}
